import { useContext, useEffect, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CircleIcon from '@mui/icons-material/Circle';
import CircularProgress from '@mui/material/CircularProgress';
import { ShipmentType, ShipmentStatusType } from '../../types/Shipment';
import { useLinkClickHandler } from 'react-router-dom';
import { exportShipments } from '../../utils/exportShipments';
import { UserContext } from '../../App';
import { isReadOnlyShipments } from '../../utils/roleHelper';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Modal, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { twMerge } from 'tailwind-merge';
import { formatDirection, formatShipmentType } from '../../utils/shipmentHelpers';

import { jwtDecode } from 'jwt-decode';
import { getAuthToken } from '../../utils/cookieUtils';
import BolComplianceReportModal from '../../components/BolComplianceReportModal';
import ShipmentsExportModal from '../../components/ShipmentsExportModal';
import { REPORTS_WEBSOCKET_URL } from '../../constants';
// Interface for facility
interface Facility {
  id: number;
  name: string;
  activation?: {
    yms?: boolean;
  };
}

const Shipments = () => {
  // @ts-ignore
  const { user, ymsEnabled } = useContext(UserContext);
  const readOnly = isReadOnlyShipments(user);
  const [shipments, setShipments] = useState<ShipmentType[]>([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [sortColumn, setSortColumn] = useState(0);
  const [sortDirection, setSortDirection] = useState(0);
  const [total, setTotal] = useState(0);
  const [shipFromList, setShipFromList] = useState([]);
  const [shipToList, setShipToList] = useState([]);
  const [carrierList, setCarrierList] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [shipTo, setShipTo] = useState([]);
  const [shipFrom, setShipFrom] = useState([]);
  const [carrier, setCarrier] = useState([]);
  const [dateType, setDateType] = useState('');
  const [dateRange, setDateRange] = useState<any>();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Facility management state
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [ymsFacilities, setYmsFacilities] = useState<Facility[]>([]);
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(null);
  const [loadingFacilities, setLoadingFacilities] = useState(false);
  
  // Add shipment modal state
  const [showAddShipmentModal, setShowAddShipmentModal] = useState(false);
  const [showBolReportModal, setShowBolReportModal] = useState(false);
  const [showShipmentsExportModal, setShowShipmentsExportModal] = useState(false);

  const hasSearch =
    search || shipFrom.length || shipTo.length || carrier.length || dateRange || selectedFacility;

  // Fetch facilities on component mount
  useEffect(() => {
    const fetchFacilities = async () => {
      setLoadingFacilities(true);
      try {
        const query = `
          query {
            facilities {
              id
              name
              activation {
                yms
              }
            }
          }
        `;

        const authToken = getAuthToken();

        const response = await fetch(process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken || user.token}`
          },
          body: JSON.stringify({
            query
          })
        });

        const result = await response.json();
        
        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        // Store all facilities for the filter dropdown
        setFacilities(result.data.facilities);
        
        // Store only YMS-enabled facilities for the add shipment modal
        const ymsEnabledFacilities = result.data.facilities.filter((facility: Facility) => 
          facility.activation?.yms === true
        );
        setYmsFacilities(ymsEnabledFacilities);
        
        // Default to null (All Facilities) if none selected
        if (!selectedFacility) {
          setSelectedFacility(null);
        }
      } catch (error) {
        console.error('Error fetching facilities:', error);
      } finally {
        setLoadingFacilities(false);
      }
    };

    if (user) {
      fetchFacilities();
    }
  }, [user]);

  useEffect(() => {
    if (hasSearch) {
      getShipments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sortColumn, sortDirection]);

  const getShipments = async () => {
    setLoading(true);
    try {
      const query = `query YmsQueryShipments($referenceNumbers: [String!]!, $facilityId: String!) {
  yms_query_shipments(reference_numbers: $referenceNumbers, facility_id: $facilityId) {
    id
    reference_number
    status
    direction
    metadata
    driver_assignment {
      id
      status
      check_in
      check_out
      driver {
        id
        firstName
        lastName
        phoneNumber
      }
    }
    # BOLs field is now available!
    bols {
      id
      estimated_bol_date
      signatures {
        id
        signature_type
      }
      deliveries {
        id
   
        ship_from {
          id
          name
          address

        }
        ship_to {
          id
          name
          address

        }
      }
    }
  }
}
      `;

      const variables = {
        referenceNumbers: search ? [search] : [],
        facilityId: selectedFacility?.id?.toString() || ""
      };

      const authToken = getAuthToken();

      const response = await fetch(process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken || user.token}`
        },
        body: JSON.stringify({
          query,
          variables
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      const transformedShipments = result.data.yms_query_shipments.map((shipment: any) => {
        const baseBol = shipment.bols?.[0];
        const baseDelivery = baseBol?.deliveries?.[0];
        const driverAssignment = shipment.driver_assignment;
        const driver = driverAssignment?.driver;
        
        // Check signatures for different types
        const signatures = baseBol?.signatures || [];
        const hasDriverSig = signatures.some((sig: any) => sig.signature_type === 'driver');
        const hasShipperSig = signatures.some((sig: any) => sig.signature_type === 'shipper');
        
        return {
          id: shipment.id,
          tnumber: shipment.reference_number,
          shipment_status: mapStatus(shipment.status),
          driver_name: driver ? `${driver.firstName} ${driver.lastName}` : '',
          direction: formatDirection(shipment.direction),
          shipment_type: formatShipmentType(shipment.metadata?.shipment_type),
          ship_from: baseDelivery?.ship_from?.name || '',
          ship_to: baseDelivery?.ship_to?.name || '',
          check_in: driverAssignment?.check_in === 'true',
          ship_sig: hasShipperSig,
          drv_sig: hasDriverSig,
          check_out: driverAssignment?.check_out === 'true',
          estimated_bol_date: baseBol?.estimated_bol_date || new Date().toISOString(),
          carrier_name: '', // Not provided in the new data structure
          photos_count: 0 // Not provided in the new data structure
        };
      });

      setShipments(transformedShipments);
      setTotal(transformedShipments.length);
      setTotalPages(Math.ceil(transformedShipments.length / perPage));
      
    } catch (error) {
      console.error('Error fetching shipments:', error);
      setError('Failed to fetch shipments');
    } finally {
      setLoading(false);
    }
  };

  const mapStatus = (status: string): ShipmentStatusType => {
    // Map GraphQL status to your application's status types
    switch (status?.toLowerCase()) {
      case 'truck_found':
        return ShipmentStatusType.truckFound;
      case 'canceled':
      case 'cancelled':
        return ShipmentStatusType.canceled;
      case 'in_transit':
      case 'intransit':
        return ShipmentStatusType.inTransit;
      default:
        return ShipmentStatusType.inTransit; 
    }
  };

  const handleSearchChange = (e: any) => {
    setSearch(e.target.value.toUpperCase());
  };

  const maxPagesToShow = 5;
  const renderPageNumbers = () => {
    const pageNumbers = [];
    const startPage = Math.max(1, page - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          className={i === page ? 'active underline font-bold' : 'hover:text-slate-400'}
          onClick={() => setPage(i)}
        >
          {i}
        </button>
      );
    }

    return pageNumbers;
  };

  const handleHeaderClick = (column: number) => {
    if (column === sortColumn) {
      setSortDirection(sortDirection === 0 ? 1 : 0);
    } else {
      setSortColumn(column);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportShipments();
    } catch (error) {
      console.error('Error exporting shipments', error);
    } finally {
      setExporting(false);
    }
  };

  const handleDateRangeChange = (option: any) => {
    if (option?.value === 'custom') {
      setDateRange(null);
      setShowDatePicker(true);
    } else if (!!option) {
      const [rangeType, rangeValue] = option.value.split('_');

      if (rangeType === 'next') {
        const endDate = new Date();
        endDate.setDate(endDate.getDate() + parseInt(rangeValue, 10));

        const range = {
          start: new Date(),
          end: endDate
        };

        setDateRange(range);
      } else if (rangeType === 'last') {
        const endDate = new Date();
        endDate.setDate(endDate.getDate() - parseInt(rangeValue, 10));

        const range = {
          start: endDate,
          end: new Date()
        };

        setDateRange(range);
      }
      if (!dateType) {
        setDateType('estimated');
      }
    } else {
      setDateRange(null);
      setDateType('');
    }
  };

  const handleCustomDateRangeChange = (dates: any) => {
    const [start, end] = dates;
    const range = {
      start: start,
      end: end
    };

    if (!dateType) {
      setDateType('estimated');
    }
    setDateRange(range);
  };

  const handleSearch = async () => {
    if (hasSearch) {
      setError('');
      
    } else {
      setError('Please enter a search term, select a facility, or choose a filter');
      return;
    }

    if (!loading && hasSearch) {
      getShipments();
    }
  };

  const shipFromOptions = shipFromList.map((shipFrom: string) => {
    return { value: shipFrom, label: shipFrom };
  });

  const shipToOptions = shipToList.map((shipTo: string) => {
    return { value: shipTo, label: shipTo };
  });

  const carrierOptions = carrierList.map((carrier: string) => {
    return { value: carrier, label: carrier };
  });

  const dateTypeOptions = [
    { value: 'estimated', label: 'Est. Ship Date' },
    { value: 'actual', label: 'Actual BOL Date' }
  ];

  const dateRangeOptions = [
    { value: 'next_1', label: 'Next 24 Hours' },
    { value: 'last_1', label: 'Last 24 Hours' },
    { value: 'next_7', label: 'Next 7 Days' },
    { value: 'last_7', label: 'Last 7 Days' },
    { value: 'last_30', label: 'Last 30 Days' },
    { value: 'last_90', label: 'Last 90 Days' },
    { value: 'custom', label: 'Custom' }
  ];

  return (
    <div className="p-5 w-full">
      <Modal
        open={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        className="flex justify-center items-center"
      >
        <div className="min-h-80">
          <DatePicker
            selected={dateRange?.start}
            onChange={handleCustomDateRangeChange}
            startDate={dateRange?.start}
            endDate={dateRange?.end}
            selectsRange
            inline
          >
            <div className="w-full text-right">
              <button
                className="bg-sky-600 hover:bg-sky-500 text-white p-2 h-[38px] rounded"
                onClick={() => setShowDatePicker(false)}
              >
                Close
              </button>
            </div>
          </DatePicker>
        </div>
      </Modal>
      <div className="bg-[#FBFBFB] p-3 shadow-[1px_0_10px_1px_rgba(0,0,0,0.3)] text-sm">
        <div className="flex justify-between mb-2">
          <div className="w-full">
            <div className="flex gap-2 items-center mb-2">
              <span className="font-bold">Facility:</span>
              <Select
                options={[
                  { value: null, label: "(*) All Facilities" },
                  ...facilities.map(facility => ({
                    value: facility,
                    label: `(${facility.id}) ${facility.name}`
                  }))
                ]}
                isSearchable={true}
                isClearable={true}
                value={selectedFacility ? {
                  value: selectedFacility,
                  label: `(${selectedFacility.id}) ${selectedFacility.name}`
                } : { value: null, label: "(*) All Facilities" }}
                placeholder="Select Facility"
                onChange={(selectedOption: any) => {
                  setSelectedFacility(selectedOption?.value || null);
                }}
                className="min-w-60"
                isLoading={loadingFacilities}
              />
            </div>
            <div className="flex gap-2 items-center">
              <Select
                styles={{
                  control: (provided) => ({
                    ...provided,
                    display: 'none'
                  })
                }}
                options={dateTypeOptions}
                isSearchable={false}
                isClearable
                placeholder="Date Type"
                className="min-w-40"
                value={dateTypeOptions.find((option) => option.value === dateType)}
                onChange={(selectedOption: any) => {
                  setDateType(selectedOption?.value);
                }}
              />
              <Select
                styles={{
                  control: (provided) => ({
                    ...provided,
                    display: 'none'
                  })
                }}
                options={dateRangeOptions}
                isSearchable={false}
                isClearable
                placeholder="Date Range"
                className="min-w-40"
                onChange={handleDateRangeChange}
                formatOptionLabel={(option: any, selectedOption: any) => {
                  if (
                    option.value === 'custom' &&
                    dateRange &&
                    selectedOption?.selectValue[0]?.value === 'custom'
                  ) {
                    const startDate = new Date(dateRange.start);
                    const endDate = new Date(dateRange.end);
                    const formattedStartDate = startDate.toLocaleDateString('en-US', {
                      month: '2-digit',
                      day: '2-digit',
                      year: '2-digit'
                    });
                    const formattedEndDate = endDate.toLocaleDateString('en-US', {
                      month: '2-digit',
                      day: '2-digit',
                      year: '2-digit'
                    });
                    return `${formattedStartDate}-${formattedEndDate}`;
                  } else {
                    return option.label;
                  }
                }}
              />
              <Select
                styles={{
                  control: (provided) => ({
                    ...provided,
                    display: 'none'
                  })
                }}
                options={carrierOptions}
                isSearchable
                isClearable
                isMulti
                placeholder="Carrier"
                className="min-w-40"
                onChange={(selectedOptions: any) => {
                  setCarrier(selectedOptions.map((option: any) => option.value));
                }}
              />
            </div>
            <div className="flex gap-2 items-center mt-2">
              <span className="font-bold">Search By TNumber:</span>
              <input
                type="search"
                className="border-slate-300 border-[1px] p-1 h-[38px] rounded"
                onChange={handleSearchChange}
                value={search}
                placeholder="Search"
              />
              <button
                className={twMerge(
                  'bg-sky-600 hover:bg-sky-500 text-white p-2 h-[38px] rounded',
                  loading && 'bg-gray-300 hover:bg-gray-300'
                )}
                onClick={handleSearch}
                disabled={loading}
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
              <Select
                styles={{
                  control: (provided) => ({
                    ...provided,
                    display: 'none'
                  })
                }}
                options={shipFromOptions}
                isSearchable
                isClearable
                isMulti
                placeholder="Ship From"
                className="min-w-40"
                onChange={(selectedOptions: any) => {
                  setShipFrom(selectedOptions.map((option: any) => option.value));
                }}
              />
              <Select
                styles={{
                  control: (provided) => ({
                    ...provided,
                    display: 'none'
                  })
                }}
                options={shipToOptions}
                isSearchable
                isClearable
                isMulti
                placeholder="Ship To"
                className="min-w-40"
                onChange={(selectedOptions: any) => {
                  setShipTo(selectedOptions.map((option: any) => option.value));
                }}
              />
            </div>
            {error && <p className="text-red-500 mt-2">{error}</p>}
          </div>
          {!readOnly && <div className="flex flex-col gap-2">
            <button
              className="bg-green-600 hover:bg-green-500 text-white p-2 h-[38px] rounded whitespace-nowrap min-w-24"
              onClick={() => setShowAddShipmentModal(true)}
            >
              Add Shipment
            </button>
            <button
              className="bg-blue-600 hover:bg-blue-500 text-white p-2 h-[38px] rounded whitespace-nowrap min-w-24"
              onClick={() => setShowShipmentsExportModal(true)}
            >
              Shipments Export
            </button>
            <button
              className="bg-blue-600 hover:bg-blue-500 text-white p-2 h-[38px] rounded whitespace-nowrap min-w-24"
              onClick={() => setShowBolReportModal(true)}
            >
              Download BOL Report
            </button>
            {/* <button
              className="bg-sky-600 hover:bg-sky-500 text-white p-2 h-[38px] rounded whitespace-nowrap min-w-24"
              disabled={exporting}
              onClick={handleExport}
            >
              {exporting ? 'Exporting...' : 'Export'}
            </button> */}
          </div>}
        </div>


        <table className="table-auto border-slate-300 border-[1px] w-full">
          <thead className="border-red-400 border-b-[3px]">
          <tr>
            <th className="p-2 cursor-pointer" onClick={() => handleHeaderClick(0)}>
              BOL#
              {sortColumn === 0 &&
                  (sortDirection === 0 ? <ExpandMoreIcon/> : <ExpandLessIcon/>)}
            </th>
            <th className="p-2">PO#</th>
            <th
                className="p-2 cursor-pointer"
                title="Shipment Status"
                onClick={() => handleHeaderClick(2)}
            >
              Status
              {sortColumn === 2 &&
                  (sortDirection === 0 ? <ExpandMoreIcon/> : <ExpandLessIcon/>)}
            </th>
            <th className="p-2" title="Exception">
              Ex.
            </th>
            <th className="p-2 cursor-pointer" onClick={() => handleHeaderClick(15)}>
              Driver Assigned
              {sortColumn === 15 &&
                  (sortDirection === 0 ? <ExpandMoreIcon/> : <ExpandLessIcon/>)}
            </th>
            <th
                className="p-2 whitespace-nowrap"
                title="Checked In"
            >
              C-I
            </th>
            <th
                className="p-2 cursor-pointer"
                title="Shipper Signature"
                onClick={() => handleHeaderClick(5)}
            >
              Ship Sig
              {sortColumn === 5 &&
                  (sortDirection === 0 ? <ExpandMoreIcon/> : <ExpandLessIcon/>)}
            </th>
            <th
                className="p-2 cursor-pointer"
                title="Driver Signature"
                onClick={() => handleHeaderClick(6)}
            >
              Drv Sig
              {sortColumn === 6 &&
                  (sortDirection === 0 ? <ExpandMoreIcon/> : <ExpandLessIcon/>)}
            </th>
            <th
                className="p-2 whitespace-nowrap"
                title="Checked Out"
            >
              C-O
            </th>
            <th className="p-2 cursor-pointer" onClick={() => handleHeaderClick(7)}>
              Ship From
              {sortColumn === 7 &&
                  (sortDirection === 0 ? <ExpandMoreIcon/> : <ExpandLessIcon/>)}
            </th>
            <th className="p-2 cursor-pointer" onClick={() => handleHeaderClick(8)}>
              Ship To
              {sortColumn === 8 &&
                  (sortDirection === 0 ? <ExpandMoreIcon/> : <ExpandLessIcon/>)}
            </th>

            <th className="p-2 cursor-pointer" onClick={() => handleHeaderClick(16)}>
              Direction - Type
              {sortColumn === 16 &&
                  (sortDirection === 0 ? <ExpandMoreIcon/> : <ExpandLessIcon/>)}
            </th>

            <th className="p-2 cursor-pointer" onClick={() => handleHeaderClick(9)}>
              Appt. Time (Local)
              {sortColumn === 9 &&
                  (sortDirection === 0 ? <ExpandMoreIcon/> : <ExpandLessIcon/>)}
            </th>
            <th className="p-2 cursor-pointer" onClick={() => handleHeaderClick(10)}>
              Carrier
              {sortColumn === 10 &&
                  (sortDirection === 0 ? <ExpandMoreIcon/> : <ExpandLessIcon/>)}
            </th>
            <th className="p-2" title="Message">
              MSG
            </th>
            <th className="p-2 cursor-pointer" onClick={() => handleHeaderClick(14)}>
              Photos
              {sortColumn === 14 &&
                  (sortDirection === 0 ? <ExpandMoreIcon/> : <ExpandLessIcon/>)}
            </th>
          </tr>
          </thead>
          {!loading && (
              <tbody className="text-center">
              {shipments.map((shipment: ShipmentType) => (
                <Row key={shipment.tnumber} shipment={shipment} />
              ))}
            </tbody>
          )}
        </table>

        {!loading && (
          <div className="flex justify-between mt-2">
            {page > 1 && (
              <button className="hover:text-slate-400" onClick={() => setPage(page - 1)}>
                {'<'} Prev
              </button>
            )}
            {renderPageNumbers()}
            {page < totalPages && (
              <button className="hover:text-slate-400" onClick={() => setPage(page + 1)}>
                Next {'>'}
              </button>
            )}
          </div>
        )}

        {loading && (
          <div className="flex flex-col items-center mt-4">
            <CircularProgress />
            <p className="mt-2">Loading...</p>
          </div>
        )}
      </div>
      
      {/* Add Shipment Modal */}
      <AddShipmentModal
        isOpen={showAddShipmentModal}
        onClose={() => setShowAddShipmentModal(false)}
        facilities={facilities}
        onShipmentCreated={() => {
          setShowAddShipmentModal(false);
          // Refresh shipments if there's an active search
          if (hasSearch) {
            getShipments();
          }
        }}
      />

      {/* Shipments Export Modal */}
      {showShipmentsExportModal && (() => {
        try {
          const authToken = getAuthToken() || '';
          if (!authToken) return null;
          const decoded: any = jwtDecode(authToken);
          const publicUserId = decoded.user_id;
          if (!publicUserId) return null;

          return (
            <ShipmentsExportModal
              open={showShipmentsExportModal}
              onClose={() => setShowShipmentsExportModal(false)}
              facilities={facilities}
              userId={publicUserId}
              jwtToken={authToken}
              websocketUrl={REPORTS_WEBSOCKET_URL}
            />
          );
        } catch (error) {
          console.error('Failed to decode JWT for shipments export:', error);
          return null;
        }
      })()}

      {/* BOL Compliance Report Modal */}
      {showBolReportModal && (() => {
        try {
          const authToken = getAuthToken() || '';
          if (!authToken) return null;
          const decoded: any = jwtDecode(authToken);
          const publicUserId = decoded.user_id;
          if (!publicUserId) return null;

          return (
            <BolComplianceReportModal
              open={showBolReportModal}
              onClose={() => setShowBolReportModal(false)}
              facilities={facilities}
              userId={publicUserId}
              jwtToken={authToken}
              websocketUrl={REPORTS_WEBSOCKET_URL}
            />
          );
        } catch (error) {
          console.error('Failed to decode JWT for BOL report:', error);
          return null;
        }
      })()}
    </div>
  );
};

interface AddShipmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  facilities: Facility[];
  onShipmentCreated: () => void;
}

const AddShipmentModal = ({ isOpen, onClose, facilities, onShipmentCreated }: AddShipmentModalProps) => {
  // @ts-ignore
  const { user } = useContext(UserContext);
  const [formData, setFormData] = useState({
    referenceNumber: '',
    facilityId: '',
    loadpointUuid: '',
    shipmentType: '', // 'live' or 'drop'
    direction: '', // 'inbound' or 'outbound'
    carrier: '',
    appointmentDate: new Date().toISOString().slice(0, 16), // datetime-local format
    mdtDate: new Date().toISOString().slice(0, 16) // datetime-local format
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validatingReference, setValidatingReference] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loadpoints, setLoadpoints] = useState<{ uuid: string; name: string; is_primary: boolean }[]>([]);
  const [loadingLoadpoints, setLoadingLoadpoints] = useState(false);

  // Fetch loadpoints when facility changes
  useEffect(() => {
    if (!formData.facilityId) {
      setLoadpoints([]);
      return;
    }

    const fetchLoadpoints = async () => {
      setLoadingLoadpoints(true);
      try {
        const authToken = getAuthToken();
        const response = await fetch(process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken || user.token}`
          },
          body: JSON.stringify({
            query: `query { loadpoints(facility_id: "${formData.facilityId}") { uuid name is_primary } }`
          })
        });
        const result = await response.json();
        const lps = result.data?.loadpoints || [];
        setLoadpoints(lps);
        // Auto-select if only one loadpoint
        if (lps.length === 1) {
          setFormData(prev => ({ ...prev, loadpointUuid: lps[0].uuid }));
        }
      } catch (err) {
        console.error('Error fetching loadpoints:', err);
        setLoadpoints([]);
      } finally {
        setLoadingLoadpoints(false);
      }
    };

    fetchLoadpoints();
  }, [formData.facilityId]);

  // Function to validate that reference number doesn't already exist
  const validateReferenceNumber = async (referenceNumber: string, facilityId: string): Promise<boolean> => {
    try {
      const authToken = getAuthToken();

      const shipmentValidationQuery = `
        query GetShipmentIdByPUID {
          shipment_by_reference_number(reference_number: "${referenceNumber.trim()}", facility_id: "${facilityId}") {
            id
          }
        }
      `;

      const validationResponse = await fetch(process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken || user.token}`
        },
        body: JSON.stringify({
          query: shipmentValidationQuery
        })
      });

      const validationResult = await validationResponse.json();
      
      if (validationResult.errors) {
        // Check if the error is "Shipment not found" - this is what we want for a new shipment
        const shipmentNotFoundError = validationResult.errors.find(
          (error: any) => error.message === "Shipment not found" && 
          error.path && error.path.includes("shipment_by_reference_number")
        );
        
        if (shipmentNotFoundError) {
          // Shipment doesn't exist, which is good for creating a new one
          return true;
        }
        
        // For other errors, throw them
        throw new Error(validationResult.errors[0].message);
      }

      // Check if shipment was found - if so, it already exists
      const shipment = validationResult.data?.shipment_by_reference_number;
      if (shipment && shipment.id) {
        return false; // Shipment already exists
      }

      // If we get here, shipment doesn't exist
      return true;
    } catch (error: any) {
      console.error('Error validating reference number:', error);
      throw error; // Throw the error so it can be caught by the caller
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.referenceNumber.trim()) {
      setError('Reference number is required');
      return;
    }
    
    if (!formData.facilityId) {
      setError('Please select a facility');
      return;
    }

    if (!formData.loadpointUuid) {
      setError('Please select a loadpoint');
      return;
    }

    if (!formData.carrier.trim()) {
      setError('Carrier SCAC is required');
      return;
    }

    if (formData.carrier.trim().length < 3 || formData.carrier.trim().length > 6) {
      setError('Carrier SCAC must be between 3 and 6 characters');
      return;
    }

    setLoading(true);
    setValidatingReference(true);
    setError('');

    try {
      // First, validate that the reference number doesn't already exist
      const referenceIsUnique = await validateReferenceNumber(formData.referenceNumber, formData.facilityId);
      setValidatingReference(false);
      
      if (!referenceIsUnique) {
        setErrorMessage(`Reference number "${formData.referenceNumber.trim().toUpperCase()}" already exists for this facility. Please use a different reference number.`);
        setShowErrorModal(true);
        setLoading(false);
        return;
      }
    } catch (validationError: any) {
      setValidatingReference(false);
      setErrorMessage(validationError.message || 'Error validating reference number');
      setShowErrorModal(true);
      setLoading(false);
      return;
    }

    // Confirm shipment creation
    const confirmed = window.confirm(
      `Are you sure you want to create shipment with reference number "${formData.referenceNumber.trim().toUpperCase()}"?`
    );
    
    if (!confirmed) {
      setLoading(false);
      return;
    }

    try {
      const mutation = `
        mutation AddSkeletonShipment($input: ShipmentInput!) {
          add_skeleton_shipment(input: $input) {
            id
            reference_number
            status
            direction
            metadata
          }
        }
      `;

      const variables = {
        input: {
          reference_number: formData.referenceNumber.trim().toUpperCase(),
          facility_id: formData.facilityId,
          loadpoint_uuid: formData.loadpointUuid,
          direction: formData.direction,
          shipment_type: formData.shipmentType === 'live' ? 'L' : 'D',
          // Keep local time value but format as ISO string (legacy tech debt - no UTC conversion)
          appointment_datetime: formData.appointmentDate + ':00.000Z',
          mdt_datetime: formData.mdtDate + ':00.000Z',
          carrier_scac: formData.carrier.toUpperCase()
        }
      };

      const authToken = getAuthToken();

      const response = await fetch(process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken || user.token}`
        },
        body: JSON.stringify({
          query: mutation,
          variables
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Reset form
      setFormData({
        referenceNumber: '',
        facilityId: '',
        loadpointUuid: '',
        shipmentType: '',
        direction: '',
        carrier: '',
        appointmentDate: new Date().toISOString().slice(0, 16),
        mdtDate: new Date().toISOString().slice(0, 16)
      });

      // Show success modal
      const createdShipment = result.data?.add_skeleton_shipment;
      setSuccessMessage(`Shipment ${createdShipment?.reference_number || formData.referenceNumber.trim().toUpperCase()} created successfully!`);
      setShowSuccessModal(true);
    } catch (error: any) {
      console.error('Error creating shipment:', error);
      setErrorMessage(error.message || 'Failed to create shipment');
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    if (field === 'facilityId') {
      setFormData(prev => ({ ...prev, facilityId: value, loadpointUuid: '' }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    setError('');
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal
        open={isOpen}
        onClose={onClose}
        className="flex justify-center items-center p-4"
      >
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Add New Shipment</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Reference Number */}
          <div>
            <label htmlFor="referenceNumber" className="block text-sm font-medium text-gray-700 mb-1">
              Reference Number *
            </label>
            <input
              id="referenceNumber"
              type="text"
              value={formData.referenceNumber}
              onChange={(e) => handleInputChange('referenceNumber', e.target.value.toUpperCase())}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter reference number"
              required
            />
          </div>

          {/* Facility Dropdown */}
          <div>
            <label htmlFor="facility" className="block text-sm font-medium text-gray-700 mb-1">
              Facility *
            </label>
            <select
              id="facility"
              value={formData.facilityId}
              onChange={(e) => handleInputChange('facilityId', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Select a facility</option>
              {facilities.map(facility => (
                <option key={facility.id} value={facility.id}>
                  ({facility.id}) {facility.name}
                </option>
              ))}
            </select>
          </div>

          {/* Loadpoint */}
          <div>
            <label htmlFor="loadpoint" className="block text-sm font-medium text-gray-700 mb-1">
              Loadpoint *
            </label>
            <select
              id="loadpoint"
              value={formData.loadpointUuid}
              onChange={(e) => handleInputChange('loadpointUuid', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
              disabled={!formData.facilityId || loadingLoadpoints}
            >
              {!formData.facilityId ? (
                <option value="">Select a facility first</option>
              ) : loadingLoadpoints ? (
                <option value="">Loading loadpoints...</option>
              ) : loadpoints.length === 0 ? (
                <option value="">No loadpoints available</option>
              ) : (
                <>
                  <option value="">Select a loadpoint</option>
                  {loadpoints.map(lp => (
                    <option key={lp.uuid} value={lp.uuid}>
                      {lp.name}{lp.is_primary ? ' (Primary)' : ''}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          {/* Shipment Type */}
          <div>
            <label htmlFor="shipmentType" className="block text-sm font-medium text-gray-700 mb-1">
              Shipment Type *
            </label>
            <select
              id="shipmentType"
              value={formData.shipmentType}
              onChange={(e) => handleInputChange('shipmentType', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Select shipment type</option>
              <option value="live">Live</option>
              <option value="drop">Drop</option>
            </select>
          </div>

          {/* Direction */}
          <div>
            <label htmlFor="direction" className="block text-sm font-medium text-gray-700 mb-1">
              Direction *
            </label>
            <select
              id="direction"
              value={formData.direction}
              onChange={(e) => handleInputChange('direction', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Select direction</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
            </select>
          </div>

           {/* Carrier */}
           <div>
             <label htmlFor="carrier" className="block text-sm font-medium text-gray-700 mb-1">
               Carrier SCAC* (3-6 characters)
             </label>
             <input 
               id="carrier" 
               type="text" 
               value={formData.carrier} 
               onChange={(e) => handleInputChange('carrier', e.target.value.toUpperCase())} 
               className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
               minLength={3}
               maxLength={6}
               placeholder="Enter SCAC code"
               required 
             />
           </div>
          {/* Appointment Date */}
          <div>
            <label htmlFor="appointmentDate" className="block text-sm font-medium text-gray-700 mb-1">
              Appointment Date *
            </label>
            <input
              id="appointmentDate"
              type="datetime-local"
              value={formData.appointmentDate}
              onChange={(e) => handleInputChange('appointmentDate', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* MDT Date */}
          <div>
            <label htmlFor="mdtDate" className="block text-sm font-medium text-gray-700 mb-1">
              MDT Date *
            </label>
            <input
              id="mdtDate"
              type="datetime-local"
              value={formData.mdtDate}
              onChange={(e) => handleInputChange('mdtDate', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm bg-red-50 p-2 rounded">
              {error}
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-2 justify-end pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || validatingReference}
              className={twMerge(
                'px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700',
                (loading || validatingReference) && 'bg-gray-400 hover:bg-gray-400 cursor-not-allowed'
              )}
            >
              {validatingReference ? 'Validating...' : loading ? 'Creating...' : 'Create Shipment'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
    
    {/* Success Modal */}
    <Dialog
      open={showSuccessModal}
      onClose={() => {
        setShowSuccessModal(false);
        onShipmentCreated();
        onClose();
      }}
    >
      <DialogTitle sx={{ color: 'green' }}>✓ Success</DialogTitle>
      <DialogContent>
        {successMessage}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setShowSuccessModal(false);
            onShipmentCreated();
            onClose();
          }}
          variant="contained"
          color="success"
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>

    {/* Error Modal */}
    <Dialog
      open={showErrorModal}
      onClose={() => setShowErrorModal(false)}
    >
      <DialogTitle sx={{ color: 'red' }}>✗ Error</DialogTitle>
      <DialogContent>
        {errorMessage}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setShowErrorModal(false)}
          variant="contained"
          color="error"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

interface RowProps {
  shipment: ShipmentType;
}

const Row = ({ shipment }: RowProps) => {
  const handleClick = useLinkClickHandler(`/shipments/${shipment.id}`);
  let statusColor = 'text-green-500';
  let statusText = 'In Transit';
  if (shipment.shipment_status === ShipmentStatusType.truckFound) {
    statusColor = 'text-yellow-500';
    statusText = 'Truck Found';
  } else if (shipment.shipment_status === ShipmentStatusType.canceled) {
    statusColor = 'text-red-500';
    statusText = 'Canceled';
  }

  return (
      <tr
          onClick={(e: any) => handleClick(e)}
          className="hover:bg-[#f5f5f5] border-slate-200 border-b-[1px] cursor-pointer odd:bg-[#f9f9f9] last:border-b-0"
      >
        <td className="p-2">{shipment.tnumber}</td>
        <td className="p-2"></td>
        <td className="p-2" title={statusText}>
          <CircleIcon className={statusColor}/>
        </td>
        <td className="p-2">
          <CircleIcon className="text-green-500"/>
        </td>
        <td className="p-2" title={shipment.driver_name}>
          {shipment.driver_name ? <CheckIcon className="text-green-500"/> : null}
        </td>
        <td className="p-2">
          {shipment.check_in ? <CheckIcon className="text-green-500"/> : null}
        </td>
        <td className="p-2">
          {shipment.ship_sig ? <CheckIcon className="text-green-500"/> : null}
        </td>
        <td className="p-2">
          {shipment.drv_sig ? <CheckIcon className="text-green-500"/> : null}
        </td>
        <td className="p-2">
          {shipment.check_out ? <CheckIcon className="text-green-500"/> : null}
        </td>
        <td className="p-2">{shipment.ship_from}</td>
        <td className="p-2">{shipment.ship_to}</td>
        <td className="p-2">{shipment.direction === 'inbound' ? 'IB':'OB'}</td>
        <td className="p-2">
          {' '}
          {new Date(shipment.estimated_bol_date).toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: '2-digit',
            hour: 'numeric',
            minute: 'numeric'
          })}
        </td>
        <td className="p-2">{shipment.carrier_name}</td>

        <td className="p-2"></td>
        <td className="p-2">{shipment.photos_count}</td>
      </tr>
  );
};

export default Shipments;
